import {getUserAssignmentProgressMapDocumentReference} from 'assignments/models/userAssignmentProgressMapReferences';
import type {ApplicationType} from 'course/application/firebase/models/applicationData';
import {
  getParentCourseId,
  type AllApplicationData,
} from 'course/application/general/utils/apputils';
import {getSectionLeaderDemoVideoUrl} from 'course/application/sectionLeaderApplication/teachingDemo/useUploadedVideo';
import {getSectionTeachingAttemptsCollectionRef} from 'course/application/sectionLeaderApplication/sectionteaching/firebase/sectionTeachingAttempt';
import {getDocs, limit, query, where} from 'firebase/firestore';
import {
  getUserLessonsProgressMapRef,
  type UserLessonsProgressMap,
} from 'course/lessons/userLessonsProgressMap';
import {getDoc} from 'firebase/firestore';
import {getUserAssignmentMapDocumentRef} from '../../../../firebase/models/userAssignmentMap';
import type {UserId, CourseId} from 'types/common';
import {getApplicantDataRef} from 'course/application/firebase/models/applicantData';
import type {FormInfo} from 'course/application/firebase/models/formData';
import type {GetTimeOnAssignmentResponse} from 'course/application/firebase/functions/getTimeOnAssignmentApi';
import type {GetTimeOnAssignmentRequest} from 'course/application/firebase/functions/getTimeOnAssignmentApi';
import {useCipHttpsCallable} from '../../../../firebase/functions/use_cip_https_callable';

export const useGetTimeOnAssignment = () => {
  return useCipHttpsCallable<
    GetTimeOnAssignmentRequest,
    GetTimeOnAssignmentResponse
  >('gettimeonassignment');
};

const getAllApplicationData = async ({
  userId,
  targetCourseId,
  applicationType,
  formInfo,
}: {
  userId: UserId;
  targetCourseId: CourseId;
  applicationType: ApplicationType;
  formInfo: FormInfo;
}): Promise<AllApplicationData> => {
  const parentCourseId = getParentCourseId(targetCourseId);
  const applicantDataRef = getApplicantDataRef({
    userId,
    courseId: targetCourseId,
    applicationType,
    formInfo,
  });
  // Application assignments are completed in the parent course.
  const userAssignmentMapRef = getUserAssignmentMapDocumentRef({
    userId,
    courseId: parentCourseId,
  });

  const userAssignmentProgressRef =
    getUserAssignmentProgressMapDocumentReference({
      userId,
      courseId: parentCourseId,
    });

  // Lessons progress is completed in the parent course. Both the student and
  // section-leader application timelines can include lesson todos (see
  // SUPPORTED_STUDENT_TODO_TYPES / SUPPORTED_SECTION_LEAD_TODO_TYPES), so this
  // is fetched for every application type — otherwise a lesson todo can never
  // be detected as complete and the timeline never reaches "complete".
  const lessonsProgressRef = getUserLessonsProgressMapRef({
    userId,
    courseId: parentCourseId,
  });

  const [
    applicantDataDoc,
    userAssignmentMapDoc,
    userAssignmentProgressDoc,
    lessonsProgressDoc,
  ] = await Promise.all([
    getDoc(applicantDataRef),
    getDoc(userAssignmentMapRef),
    getDoc(userAssignmentProgressRef),
    getDoc(lessonsProgressRef),
  ]);

  const applicantData = applicantDataDoc.exists()
    ? applicantDataDoc.data()
    : null;
  const userAssignmentMap = userAssignmentMapDoc.exists()
    ? userAssignmentMapDoc.data()
    : null;
  const userAssignmentProgress = userAssignmentProgressDoc.exists()
    ? userAssignmentProgressDoc.data()
    : null;
  const lessonsProgress: UserLessonsProgressMap | undefined =
    lessonsProgressDoc.exists() ? lessonsProgressDoc.data() : undefined;

  ////////////////////////////////////////////////
  // Section Leader only
  ////////////////////////////////////////////////
  let videoUrl: string | undefined = undefined;
  let sectionTeachingComplete: boolean | undefined = undefined;
  if (applicationType === 'sl') {
    // The section-teaching step counts as complete once the applicant has one
    // attempt they finished by answering the student's closing question — the
    // `completesectionteachingattempt` callable marks that attempt `completed`.
    // These two reads are independent, so run them concurrently rather than
    // paying for two sequential round trips. Each is wrapped in its own
    // async/try so a synchronous throw (as well as a rejected promise)
    // degrades that one field instead of failing the whole batch.
    [videoUrl, sectionTeachingComplete] = await Promise.all([
      (async () => {
        try {
          return await getSectionLeaderDemoVideoUrl(userId);
        } catch {
          console.warn(`No video found for ${userId}`);
          return undefined;
        }
      })(),
      (async () => {
        try {
          const completedSectionAttempts = await getDocs(
            query(
              getSectionTeachingAttemptsCollectionRef(userId, targetCourseId),
              where('status', '==', 'completed'),
              limit(1),
            ),
          );
          return !completedSectionAttempts.empty;
        } catch (attemptsError) {
          console.warn(
            `Could not read section-teaching attempts for ${userId}`,
            attemptsError,
          );
          return undefined;
        }
      })(),
    ]);
  }
  return {
    appData: applicantData,
    userAssignmentMap,
    userAssignmentProgress,
    ...(lessonsProgress ? {lessonsProgress} : {}),
    ...(videoUrl ? {videoUrl} : {}),
    ...(sectionTeachingComplete !== undefined ? {sectionTeachingComplete} : {}),
  };
};

export default getAllApplicationData;
